export type InventoryCategory = "clothing" | "artifact" | "tool" | "decoration" | "misc";

export interface ItemProvenance {
  readonly foundAt?: string;
  readonly foundInRegion?: string;
  readonly discoveredBy?: string;
  readonly discoveredAt?: number;
}

/** An owned item. `definitionId` points at static content; this is the player-specific instance. */
export interface InventoryItem {
  readonly instanceId: string;
  readonly definitionId: string;
  readonly quantity: number;
  readonly category: InventoryCategory;
  readonly stackable: boolean;
  readonly favorite: boolean;
  readonly provenance?: ItemProvenance;
  readonly metadata?: Readonly<Record<string, unknown>>;
}

export interface Inventory {
  readonly items: readonly InventoryItem[];
}

export function emptyInventory(): Inventory {
  return { items: [] };
}

export function addItem(inventory: Inventory, item: InventoryItem): Inventory {
  if (item.stackable) {
    const existing = inventory.items.find((i) => i.definitionId === item.definitionId);
    if (existing) {
      return {
        items: inventory.items.map((i) =>
          i.instanceId === existing.instanceId ? { ...i, quantity: i.quantity + item.quantity } : i,
        ),
      };
    }
  }
  return { items: [...inventory.items, item] };
}

export function removeItem(inventory: Inventory, instanceId: string, quantity = 1): Inventory {
  const existing = inventory.items.find((i) => i.instanceId === instanceId);
  if (!existing) return inventory;
  if (existing.quantity > quantity) {
    return {
      items: inventory.items.map((i) =>
        i.instanceId === instanceId ? { ...i, quantity: i.quantity - quantity } : i,
      ),
    };
  }
  return { items: inventory.items.filter((i) => i.instanceId !== instanceId) };
}

export function findItem(inventory: Inventory, instanceId: string): InventoryItem | undefined {
  return inventory.items.find((i) => i.instanceId === instanceId);
}

export function setFavorite(inventory: Inventory, instanceId: string, favorite: boolean): Inventory {
  return {
    items: inventory.items.map((i) => (i.instanceId === instanceId ? { ...i, favorite } : i)),
  };
}
